// /app/frontend/src/pages/admin/OrgMFAPage.jsx
// DPO org admin MFA management — org-wide policy, per-user MFA requirement, reset
import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Shield, ShieldCheck, ShieldOff, Search, RotateCcw,
  Users, AlertTriangle, Loader2, Lock, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { mfaOrgAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export default function OrgMFAPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [policy, setPolicy] = useState({ mfa_required: false });
  const [loading, setLoading] = useState(true);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmUser, setConfirmUser] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [togglingUser, setTogglingUser] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, policyRes] = await Promise.all([
        mfaOrgAPI.getUsers(),
        mfaOrgAPI.getPolicy(),
      ]);
      setUsers(usersRes.data);
      setPolicy(policyRes.data);
    } catch { toast.error('Failed to load MFA data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePolicyToggle = async (value) => {
    setPolicyLoading(true);
    const prev = policy;
    setPolicy({ mfa_required: value });
    try {
      await mfaOrgAPI.updatePolicy({ mfa_required: value });
      toast.success(value ? 'MFA enforcement enabled for your organisation' : 'MFA enforcement disabled');
    } catch {
      setPolicy(prev);
      toast.error('Failed to update MFA policy');
    } finally { setPolicyLoading(false); }
  };

  const handleRequireToggle = async (user, value) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot change MFA requirements for yourself');
      return;
    }
    setTogglingUser(user.id);
    try {
      await mfaOrgAPI.requireUser(user.id, { mfa_required: value });
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, mfa_required_individual: value } : u
      ));
      toast.success(value
        ? `MFA now required for ${user.full_name || user.email}`
        : `MFA requirement removed for ${user.full_name || user.email}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user MFA requirement');
    } finally { setTogglingUser(null); }
  };

  const handleReset = async () => {
    if (!confirmUser) return;
    if (confirmUser.id === currentUser?.id) {
      toast.error('You cannot reset MFA for yourself');
      setConfirmUser(null);
      return;
    }
    setResetting(true);
    try {
      await mfaOrgAPI.resetUser(confirmUser.id);
      toast.success(`MFA reset for ${confirmUser.email}`);
      setConfirmUser(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset MFA');
    } finally { setResetting(false); }
  };

  const filtered = users.filter(u =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: users.length,
    enabled: users.filter(u => u.mfa_enabled).length,
    disabled: users.filter(u => !u.mfa_enabled).length,
    required: users.filter(u => u.mfa_required_individual).length,
  };
  const coveragePct = stats.total > 0 ? Math.round((stats.enabled / stats.total) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="mfa-mgmt-title">
            <Lock className="h-6 w-6 text-violet-400" />
            MFA Management
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Control two-factor authentication for users in your organisation
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.total, icon: Users, color: 'text-slate-400' },
            { label: 'MFA Enabled', value: `${stats.enabled} (${coveragePct}%)`, icon: ShieldCheck, color: 'text-emerald-400' },
            { label: 'MFA Disabled', value: stats.disabled, icon: ShieldOff, color: 'text-rose-400' },
            { label: 'Individually Required', value: stats.required, icon: UserCheck, color: 'text-violet-400' },
          ].map(s => (
            <Card key={s.label} className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <p className="text-xl font-bold text-white mt-0.5">{s.value}</p>
                </div>
                <s.icon className={cn('h-7 w-7 opacity-60', s.color)} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Org-Wide Policy Card */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-400" />
              Organisation-Wide MFA Policy
              {policyLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </CardTitle>
            <CardDescription>
              Enforce MFA for all users at once. For selective enforcement, use the per-user toggle below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/40 border border-slate-700">
              <div>
                <Label className="text-white font-medium">Require MFA for all users</Label>
                <p className="text-sm text-slate-400 mt-0.5">
                  Users without MFA will be blocked from the dashboard until they enrol
                </p>
              </div>
              <Switch
                data-testid="org-mfa-policy-switch"
                checked={policy.mfa_required}
                onCheckedChange={handlePolicyToggle}
                disabled={policyLoading}
              />
            </div>

            {policy.mfa_required && stats.disabled > 0 && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm text-amber-400">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>{stats.disabled} user{stats.disabled !== 1 ? 's' : ''}</strong> in your organisation
                  do not have MFA set up. They will be prompted to enrol on their next login.
                </span>
              </div>
            )}

            {policy.mfa_required && stats.disabled === 0 && (
              <div className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-sm text-emerald-400">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                All users in your organisation have MFA enabled.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">User MFA Status</CardTitle>
                <CardDescription>Require MFA per user, or reset existing MFA enrolments</CardDescription>
              </div>
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="pl-9 bg-slate-900 border-slate-700"
                  data-testid="mfa-user-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>MFA Status</TableHead>
                    <TableHead>Require MFA</TableHead>
                    <TableHead className="w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : filtered.map(u => {
                    const isRequired = policy.mfa_required || u.mfa_required_individual;
                    return (
                      <TableRow key={u.id} className={cn(!u.mfa_enabled && isRequired && 'bg-amber-500/5')}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-white text-sm">{u.full_name || '\u2014'}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {u.role?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.mfa_enabled ? (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1">
                              <ShieldCheck className="h-3 w-3" /> Enabled
                            </Badge>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/20 gap-1">
                                <ShieldOff className="h-3 w-3" /> Disabled
                              </Badge>
                              {isRequired && (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                                  Will be prompted
                                </Badge>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {policy.mfa_required ? (
                            <span className="text-xs text-slate-500">Org policy</span>
                          ) : u.id === currentUser?.id ? (
                            <span className="text-xs text-slate-500">Your account</span>
                          ) : (
                            <Switch
                              data-testid={`require-mfa-${u.id}`}
                              checked={u.mfa_required_individual}
                              onCheckedChange={(val) => handleRequireToggle(u, val)}
                              disabled={togglingUser === u.id}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {u.mfa_enabled && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                'text-xs',
                                u.id === currentUser?.id
                                  ? 'text-slate-400 border-slate-500/20 cursor-not-allowed opacity-50'
                                  : 'text-amber-400 border-amber-500/30 hover:bg-amber-500/10'
                              )}
                              onClick={() => setConfirmUser(u)}
                              disabled={u.id === currentUser?.id}
                              data-testid={`reset-mfa-${u.id}`}
                              title={u.id === currentUser?.id ? 'Cannot reset MFA for your own account' : 'Reset MFA for this user'}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" /> Reset
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Reset Dialog */}
      <Dialog open={!!confirmUser} onOpenChange={() => setConfirmUser(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Reset MFA
            </DialogTitle>
            <DialogDescription>
              This will disable MFA for <strong className="text-white">{confirmUser?.email}</strong>.
              They will need to re-enrol on their next login if MFA is required for them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmUser(null)} disabled={resetting}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleReset} disabled={resetting} data-testid="confirm-reset-btn">
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Confirm Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
